'use client'

import { useEffect, useState, useRef, useCallback } from 'react'

const STORAGE_KEY = 'mallard-theme'
const MODES = [
    { key: 'system', label: 'System' },
    { key: 'dark', label: 'Dark' },
    { key: 'light', label: 'Light' },
]

function resolveSystemTheme() {
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function applyTheme(mode) {
    const resolved = mode === 'system' ? resolveSystemTheme() : mode
    document.documentElement.setAttribute('data-theme', resolved)
    window.dispatchEvent(new CustomEvent('mallard:themechange', { detail: { theme: resolved, mode } }))
}

function MoonIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M14.3 10.5A6.5 6.5 0 0 1 5.5 1.7a6.5 6.5 0 1 0 8.8 8.8Z" fill="currentColor"/>
        </svg>
    )
}

function SunIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="3.2" fill="currentColor"/>
            <g stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                <line x1="8" y1="0.8" x2="8" y2="2.4"/>
                <line x1="8" y1="13.6" x2="8" y2="15.2"/>
                <line x1="0.8" y1="8" x2="2.4" y2="8"/>
                <line x1="13.6" y1="8" x2="15.2" y2="8"/>
                <line x1="2.9" y1="2.9" x2="4.1" y2="4.1"/>
                <line x1="11.9" y1="11.9" x2="13.1" y2="13.1"/>
                <line x1="2.9" y1="13.1" x2="4.1" y2="11.9"/>
                <line x1="11.9" y1="4.1" x2="13.1" y2="2.9"/>
            </g>
        </svg>
    )
}

function MonitorIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1.5" y="2.5" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" fill="none"/>
            <line x1="5.5" y1="14" x2="10.5" y2="14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="8" y1="11.5" x2="8" y2="14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
    )
}

const ICONS = { system: MonitorIcon, dark: MoonIcon, light: SunIcon }

export default function ThemeToggle() {
    const [mode, setMode] = useState(null)
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)
    const triggerRef = useRef(null)
    const optionRefs = useRef([])

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY)
        const initial = MODES.some((m) => m.key === stored) ? stored : 'system'
        setMode(initial)

        const mql = window.matchMedia('(prefers-color-scheme: light)')
        const handleChange = () => {
            const current = localStorage.getItem(STORAGE_KEY)
            if (!current || current === 'system') {applyTheme('system')}
        }
        mql.addEventListener('change', handleChange)
        return () => mql.removeEventListener('change', handleChange)
    }, [])

    // Close and hand focus back to the trigger so keyboard users keep their place
    const close = useCallback((returnFocus) => {
        setOpen(false)
        if (returnFocus) {triggerRef.current?.focus()}
    }, [])

    // On open: focus the active option. While open: outside click / Escape close it.
    useEffect(() => {
        if (!open) {return}
        const activeIndex = Math.max(0, MODES.findIndex((m) => m.key === mode))
        optionRefs.current[activeIndex]?.focus()

        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {close(false)}
        }
        const handleEscape = (e) => {
            if (e.key === 'Escape') {close(true)}
        }
        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleEscape)
        }
        // mode is read once on open; re-running on select would steal focus
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, close])

    const select = useCallback((key) => {
        setMode(key)
        applyTheme(key)
        try { localStorage.setItem(STORAGE_KEY, key) } catch { /* ignore */ }
        close(true)
    }, [close])

    // Arrow / Home / End move between the three options
    const handleMenuKeyDown = (e) => {
        const items = optionRefs.current.filter(Boolean)
        const i = items.indexOf(document.activeElement)
        let next = null
        if (e.key === 'ArrowDown') {next = (i + 1) % items.length}
        else if (e.key === 'ArrowUp') {next = (i - 1 + items.length) % items.length}
        else if (e.key === 'Home') {next = 0}
        else if (e.key === 'End') {next = items.length - 1}
        if (next === null) {return}
        e.preventDefault()
        items[next]?.focus()
    }

    if (mode === null) {
        return <div className="theme-dropdown" aria-hidden="true" style={{ width: 40, height: 40 }} />
    }

    const CurrentIcon = ICONS[mode]

    return (
        <div className="theme-dropdown" ref={wrapRef}>
            <button
                ref={triggerRef}
                type="button"
                className="theme-dropdown__trigger"
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true) }
                }}
                aria-expanded={open}
                aria-controls="theme-dropdown-menu"
                aria-label={`Theme: ${MODES.find((m) => m.key === mode)?.label}`}
            >
                <CurrentIcon />
            </button>
            {open && (
                <ul
                    id="theme-dropdown-menu"
                    className="theme-dropdown__menu"
                    aria-label="Theme"
                    onKeyDown={handleMenuKeyDown}
                >
                    {MODES.map(({ key, label }, index) => {
                        const Icon = ICONS[key]
                        return (
                            <li key={key}>
                                <button
                                    ref={(el) => { optionRefs.current[index] = el }}
                                    type="button"
                                    className={`theme-dropdown__option${mode === key ? ' is-active' : ''}`}
                                    aria-pressed={mode === key}
                                    onClick={() => select(key)}
                                >
                                    <Icon />
                                    <span>{label}</span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}
